import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useFavorites } from '../../context/FavoritesContext';
import ProductCard from '../ProductCard';
import './TopItemsSection.css';

const STORAGE_KEY = 'topItemsCollapsed';

function TopItemsSection({
  topItems,
  onAddToCart,
  onItemClick,
  getTranslatedText,
  restaurantId,
  tableNumber,
  mode,
}) {
  const { t } = useTranslation();
  const { isFavorite, toggleFavorite } = useFavorites();
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [totalDots, setTotalDots] = useState(0);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === 'true';
    } catch {
      return false;
    }
  });

  const handleToggleCollapse = useCallback(() => {
    setCollapsed(prev => {
      const next = !prev;
      try {
        sessionStorage.setItem(STORAGE_KEY, String(next));
      } catch {
        // Ignore sessionStorage errors
      }
      return next;
    });
  }, []);

  const calculateDots = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      setTotalDots(0);
      return;
    }
    const pages = Math.ceil(el.scrollWidth / el.clientWidth);
    setTotalDots(pages);
  }, []);

  const updateActiveIndex = useCallback(() => {
    const el = carouselRef.current;
    if (!el || totalDots <= 1) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) return;
    const progress = el.scrollLeft / maxScroll;
    const index = Math.round(progress * (totalDots - 1));
    setActiveIndex(index);
  }, [totalDots]);

  useEffect(() => {
    if (collapsed) return;
    calculateDots();
    window.addEventListener('resize', calculateDots);
    return () => window.removeEventListener('resize', calculateDots);
  }, [calculateDots, topItems, collapsed]);

  useEffect(() => {
    if (collapsed) return;
    const el = carouselRef.current;
    if (!el) return;
    const handleScroll = () => updateActiveIndex();
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, [updateActiveIndex, collapsed]);

  const handleDotClick = (index) => {
    const el = carouselRef.current;
    if (!el || totalDots <= 1) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const scrollTo = (index / (totalDots - 1)) * maxScroll;
    el.scrollTo({ left: scrollTo, behavior: 'smooth' });
  };

  if (!topItems || topItems.length === 0) {
    return null;
  }

  return (
    <section className="top-items-section">
      <div className="top-items-header">
        <h2 className="top-items-title">
          <span className="material-icons" aria-hidden="true">local_fire_department</span>
          {t('topItems.title')}
        </h2>
        <button
          className="top-items-collapse-btn"
          onClick={handleToggleCollapse}
          aria-expanded={!collapsed}
          aria-label={collapsed ? t('topItems.expand') : t('topItems.collapse')}
        >
          <span className="material-icons" aria-hidden="true">
            {collapsed ? 'expand_more' : 'expand_less'}
          </span>
        </button>
      </div>

      {!collapsed && (
        <>
          <div className="top-items-carousel" ref={carouselRef}>
            {topItems.map((item) => (
              <div key={item.itemId} className="top-item-card-wrapper">
                <ProductCard
                  item={item}
                  viewMode="grid"
                  onAddToCart={onAddToCart}
                  onItemClick={onItemClick}
                  getTranslatedText={getTranslatedText}
                  isFavorite={isFavorite(item.itemId)}
                  onToggleFavorite={toggleFavorite}
                  restaurantId={restaurantId}
                  tableNumber={tableNumber}
                  mode={mode}
                />
              </div>
            ))}
          </div>

          {totalDots > 1 && (
            <div className="top-items-dots" role="tablist" aria-label={t('topItems.carouselPages')}>
              {Array.from({ length: totalDots }, (_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`top-items-dot${i === activeIndex ? ' top-items-dot--active' : ''}`}
                  role="tab"
                  aria-selected={i === activeIndex}
                  aria-label={`Page ${i + 1}`}
                  onClick={() => handleDotClick(i)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

export default TopItemsSection;
